import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fecha } from "../lib/formato";
import { useIdioma } from "../lib/idioma";

/**
 * El contrato, en el móvil del socio.
 *
 * Lo que ve es **lo que firmó**, con sus datos dentro. Si el club lo ha
 * cambiado desde entonces se le dice, pero no se le cuela el texto nuevo como
 * si fuera el suyo: firmó aquello, no esto.
 *
 * El texto del contrato es el del club, en el idioma en que el club lo
 * escribió: aquí solo se traduce lo de alrededor.
 */

interface Contrato {
  hay: boolean;
  firmado?: boolean;
  aceptadoEn?: string | null;
  numero?: number;
  hayVersionNueva?: boolean;
  texto?: string;
}

export default function PortalContrato() {
  const clienteConsultas = useQueryClient();
  const { t, delFallo } = useIdioma();
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");

  const contrato = useQuery({
    queryKey: ["portal-contrato"],
    queryFn: () => api<Contrato>("/portal/contrato"),
  });

  async function aceptar() {
    setError("");
    setEnviando(true);
    try {
      await api("/portal/contrato/aceptar", { metodo: "POST" });
      void clienteConsultas.invalidateQueries({
        queryKey: ["portal-contrato"],
      });
    } catch (fallo) {
      setError(delFallo(fallo, "contrato.falloGuardar"));
    } finally {
      setEnviando(false);
    }
  }

  if (contrato.isLoading)
    return <div className="cargando">{t("general.cargando")}</div>;
  const datos = contrato.data;

  if (!datos?.hay) {
    return (
      <section className="portal-tarjeta">
        <p className="suave" style={{ margin: 0 }}>
          {t("contrato.sinPublicar")}
        </p>
      </section>
    );
  }

  return (
    <>
      <section className="portal-tarjeta">
        {datos.firmado ? (
          <p style={{ margin: 0 }}>
            {t("contrato.aceptaste")}{" "}
            <strong>
              {t("contrato.version", { numero: datos.numero ?? "" })}
            </strong>
            {datos.aceptadoEn &&
              ` ${t("contrato.elDia", { fecha: fecha(datos.aceptadoEn) })}`}
            {t("contrato.esLaQueAplica")}
          </p>
        ) : (
          <p style={{ margin: 0 }}>{t("contrato.leeConCalma")}</p>
        )}

        {datos.hayVersionNueva && (
          <p className="suave pequeno" style={{ marginBottom: 0 }}>
            {t("contrato.versionNueva")}
          </p>
        )}
      </section>

      <section className="portal-tarjeta">
        <pre className="texto-contrato">{datos.texto}</pre>
      </section>

      {!datos.firmado && (
        <section className="portal-tarjeta">
          {error && <p className="aviso error">{error}</p>}
          <button
            className="boton grande"
            disabled={enviando}
            onClick={aceptar}
            style={{ width: "100%" }}
          >
            {enviando ? t("general.guardando") : t("contrato.aceptar")}
          </button>
        </section>
      )}
    </>
  );
}
